import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Grid,
  Button,
  Divider,
  DialogContent,
  DialogActions
} from '@mui/material';
import theme from 'src/theme';
import Loader from 'src/common/Loader';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { GROUP_TYPES } from 'src/utils/constants';
import { addNewVehicle, updateVehicleDetails } from 'src/redux/vehicleSlice';
import { getAllSites } from 'src/redux/siteSlice';
import { getGroupList } from 'src/redux/groupSlice';
import { SwitchInput } from 'src/common/FormElements';
import InputField from 'src/common/formFields/InputField';
import FMSModal, { FMSModalTitle } from 'src/common/FMSModal';
import SelectField from 'src/common/formFields/SelectField';
import { getAllTanks } from 'src/redux/tankSlice';
import {
  vehicleFormModel,
  vehicleFormInitialValues,
  vehicleValidationSchema
} from './vehicleFormModel';

const NewVehicle = ({ open, handleClose, initialValues }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [vehicleGroupList, setVehicleGroupList] = useState([]);
  const { allSites } = useSelector((state) => state.sites);
  const { allTanks } = useSelector((state) => state.tanks);

  const formInitValues = initialValues || vehicleFormInitialValues;
  const NULL_GROUP_OPTION = { id: null, name: '--------' };

  const withNullOption = (list = []) => [NULL_GROUP_OPTION, ...list];

  const {
    formId,
    formField: {
      vehicleGroup,
      site,
      department,
      vehicleCategory,
      transferTank,
      registrationNum,
      vehicleType,
      assetCode,
      assetType,
      tankCapacity,
      telemeticsEnabled,
      telemeticsCategory,
      serviceInterval,
      vehicleModel,
      fuelType,
      maxDiffAllowed,
      odometerReading,
      monthlyFixedEngineHours
    }
  } = vehicleFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllSites());
      dispatch(getAllTanks());
      dispatch(getGroupList({ groupType: GROUP_TYPES[1].id }))
        .unwrap()
        .then((list) => setDepartmentList(withNullOption(list)));
      dispatch(getGroupList({ groupType: GROUP_TYPES[0].id }))
        .unwrap()
        .then((list) => setVehicleGroupList(withNullOption(list)));
    }
  }, [open, dispatch]);

  const onSubmit = async (values, actions) => {
    const payload = { ...values };
    if (!values.telemetics_enabled) {
      payload.telemetics_category = '';
    }

    setLoading(true);
    try {
      if (initialValues) {
        await dispatch(updateVehicleDetails(payload)).unwrap();
      } else {
        await dispatch(addNewVehicle(payload)).unwrap();
      }
      handleClose();
      dispatch(
        setSuccessInfo({
          successMsg: initialValues
            ? 'Vehicle details updated successfully!'
            : 'New vehicle has been created successfully!'
        })
      );
    } catch (error) {
      actions.setSubmitting(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <FMSModalTitle
        title={initialValues ? 'UPDATE VEHICLE' : 'ADD NEW VEHICLE'}
        onCancel={handleClose}
      />

      <Divider />

      {loading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={formInitValues}
          validationSchema={vehicleValidationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form id={formId}>
              {/* SCROLLABLE CONTENT */}
              <DialogContent
                sx={{
                  maxHeight: '65vh',
                  overflowY: 'auto',
                  px: 3,
                  py: 2,
                  backgroundColor: theme.palette.background.default
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InputField name={registrationNum.name} label={registrationNum.label} fullWidth />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={vehicleType.name}
                      label={vehicleType.label}
                      data={vehicleType.data}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField name={assetCode.name} label={assetCode.label} upperCase fullWidth />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField name={tankCapacity.name} label={tankCapacity.label} fullWidth />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={vehicleCategory.name}
                      label={vehicleCategory.label}
                      data={vehicleCategory.data}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={transferTank.name}
                      label={transferTank.label}
                      data={allTanks}
                      disabled={values[vehicleCategory.name] !== 'TRNSFR'}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField name={vehicleModel.name} label={vehicleModel.label} fullWidth />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField name={site.name} label={site.label} data={allSites} fullWidth />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={fuelType.name}
                      label={fuelType.label}
                      data={fuelType.data}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={vehicleGroup.name}
                      label={vehicleGroup.label}
                      data={vehicleGroupList}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={department.name}
                      label={department.label}
                      data={departmentList}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SwitchInput name={telemeticsEnabled.name} label={telemeticsEnabled.label} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={telemeticsCategory.name}
                      label={telemeticsCategory.label}
                      data={telemeticsCategory.data}
                      disabled={!values[telemeticsEnabled.name]}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField name={odometerReading.name} label={odometerReading.label} fullWidth />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={monthlyFixedEngineHours.name}
                      label={monthlyFixedEngineHours.label}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={serviceInterval.name}
                      label={serviceInterval.label}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <InputField
                      name={maxDiffAllowed.name}
                      label={maxDiffAllowed.label}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SelectField
                      name={assetType.name}
                      label={assetType.label}
                      data={assetType.data}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </DialogContent>

              <Divider />

              {/* STICKY FOOTER */}
              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: theme.palette.background.paper,
                  justifyContent: 'flex-end',
                  gap: 2
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleClose}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 4,
                    fontWeight: 600
                  }}
                >
                  {initialValues ? 'Update Vehicle' : 'Add Vehicle'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </FMSModal>
  );
};

NewVehicle.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewVehicle;
